import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { portalAuthHeaderValue } from '@/utils/tokenUtils';
import { toast } from 'sonner';
import { FileText, Loader2, UploadCloud, CheckCircle2, Sparkles } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Schritt im Onboarding: Verordnung hochladen → KI liest aus → Profilfelder
// werden vorausgefüllt. onComplete(prefill, extracted) / onSkip().
const PrescriptionUploadStep = ({ open, onComplete, onSkip }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/portal/prescriptions/upload`, fd, {
        headers: { Authorization: portalAuthHeaderValue() },
      });
      const ex = r.data?.extracted || {};
      const prefill = {
        first_name: ex.patient_first_name || '',
        last_name: ex.patient_last_name || '',
        birthdate: ex.patient_birthdate || '',
        street: ex.patient_street || '',
        zip: ex.patient_zip || '',
        city: ex.patient_city || '',
      };
      setDone(true);
      try { localStorage.setItem('onboarding_has_prescription', 'done'); } catch { /* noop */ }
      const hasAny = Object.values(prefill).some((v) => v);
      toast.success(hasAny
        ? t('portal.rxupload.ok', 'Verordnung erkannt – deine Daten wurden übernommen.')
        : t('portal.rxupload.ok_nodata', 'Verordnung hochgeladen. Bitte ergänze deine Daten.'));
      setTimeout(() => onComplete && onComplete(prefill, ex, r.data?.id), 700);
    } catch (err) {
      toast.error(t('portal.rxupload.err', 'Upload fehlgeschlagen. Bitte versuche es erneut.'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const skip = () => {
    try { localStorage.setItem('onboarding_has_prescription', 'skipped'); } catch { /* noop */ }
    onSkip && onSkip();
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* erzwungen, kein Schliessen per Klick aussen */ }}>
      <DialogContent className="bg-gray-900 border-gray-700 text-white sm:max-w-md" data-testid="rx-upload-step">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <FileText className="h-5 w-5 text-amber-400" />
            {t('portal.rxupload.title', 'Verordnung hochladen')}
          </DialogTitle>
          <DialogDescription className="text-white/70">
            {t('portal.rxupload.desc', 'Lade ein Foto oder PDF deiner ärztlichen Verordnung hoch. Unsere KI liest sie aus und füllt deine Daten automatisch aus.')}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {done ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="rx-upload-done">
              <CheckCircle2 className="h-12 w-12 text-emerald-400" />
              <p className="text-white/80">{t('portal.rxupload.done', 'Erkannt! Weiter zu deinem Profil …')}</p>
            </div>
          ) : uploading ? (
            <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="rx-upload-loading">
              <Loader2 className="h-10 w-10 animate-spin text-amber-400" />
              <p className="flex items-center gap-2 text-white/80">
                <Sparkles className="h-4 w-4 text-amber-400" />
                {t('portal.rxupload.loading', 'KI liest deine Verordnung aus …')}
              </p>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="rx-upload-dropzone"
              className="w-full rounded-xl border-2 border-dashed border-gray-600 hover:border-amber-500/70 bg-gray-800/50 hover:bg-gray-800 transition-colors p-8 flex flex-col items-center gap-3"
            >
              <UploadCloud className="h-10 w-10 text-amber-400" />
              <span className="font-semibold text-white">{t('portal.rxupload.cta', 'Datei auswählen')}</span>
              <span className="text-sm text-white/60">{t('portal.rxupload.hint', 'JPG, PNG oder PDF · max. 15 MB')}</span>
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            className="hidden"
            onChange={handleFile}
            data-testid="rx-upload-input"
          />
        </div>

        {!uploading && !done && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={skip}
              data-testid="rx-upload-skip"
              className="text-sm text-white/50 hover:text-white underline underline-offset-2"
            >
              {t('portal.rxupload.skip', 'Keine Verordnung – Daten selbst eingeben')}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PrescriptionUploadStep;
